<template>
  <div>
    <ConfirmationDialog2 ref="confirmationDialog" />
    <v-dialog v-model="dialog" :fullscreen="$vuetify.breakpoint.smAndDown" persistent content-class="save-query-dialog" max-width="600px">
      <div v-if="dialog" class="save-dialog">
        <div class="save-dialog-head">
          <h2 class="save-dialog-title">Save query</h2>
          <v-btn icon small class="save-dialog-x" :disabled="loading.saving" aria-label="Close save query dialog" @click.native="close()">
            <v-icon small>close</v-icon>
          </v-btn>
        </div>

        <div class="save-dialog-content">
          <div v-if="nameCheckError" class="save-state save-state-warning" role="status" aria-live="polite">
            {{ nameCheckError }}
          </div>
          <div v-else class="save-state" :class="{ 'save-state-warning': isDestructive }" role="status" aria-live="polite">
            <span v-if="loading.name">Checking for an existing query…</span>
            <span v-else>{{ saveStateMessage }}</span>
          </div>

          <Form :disabled="loading.saving" ref="form" @update:formData="formData = $event" @update:isValid="isValid = $event" @update:selectedUser="selectedUser = $event" />
        </div>

        <div class="save-dialog-foot">
          <span class="save-dialog-feedback" aria-live="polite">{{ feedbackText }}</span>
          <span class="save-dialog-spacer"></span>
          <v-btn class="save-submit" :class="{ 'save-submit-warning': isDestructive }" depressed :loading="loading.saving" :disabled="submitDisabled" @click="submit()">
            <v-icon small left>save</v-icon>
            {{ submitLabel }}
          </v-btn>
          <v-btn class="save-cancel" outlined :disabled="loading.saving" @click="close()">
            <v-icon small left>close</v-icon>
            {{$t('Button.Cancel')}}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
  import Form from './Form'
  import {
    saveStates,
  } from '@/helpers/multiStationQuery'

  import {
    debounce,
  } from '@/helpers/performance'
  const debounceTimer = 600

  export default {
    components: {
      Form,
    },
    props: {
      service: Object,
    },
    data: () => ({
      saveStates,
      nameExists: false,
      nameCheckError: '',
      nameCheckSequence: 0,
      selectedUser: null,
      dialog: false,
      formData: null,
      feedbackText: '',
      initial: {
        id: null,
        name: null,
        userId: null,
      },

      queryDetails: null,
      queryData: null,
      optionsData: null,
      isValid: false,
      loading: {
        name: false,
        saving: false,
      },
    }),
    computed: {
      reportGenerator() {
        return this.$store.getters['multistation/reportGenerator']
      },
      currentUser() {
        return this.$store.getters['authenticate/currentUser']
      },
      selectedUsername() {
        if (this.isCurrentUser(this.selectedUser)) return 'you'
        else return this.selectedUser && (this.selectedUser.username || this.selectedUser.UserName)
      },
      isSomeAdmin() {
        return this.$store.getters['authenticate/isSomeAdmin']
      },
      saveState() {
        if (this.nameExists) {
          if (this.initial.name == this.formData.name &&
            this.initial.userId == this.formData.userId) {
            return saveStates.updateExisting
          }

          return saveStates.overwriteExisting
        }

        return saveStates.createNew
      },
      isDestructive() {
        return this.saveState == saveStates.overwriteExisting
      },
      saveStateMessage() {
        const username = this.selectedUsername

        if (this.saveState == saveStates.updateExisting) {
          if (this.isSomeAdmin && !this.isCurrentUser(this.selectedUser)) {
            return `The selected query will be updated for ${username}.`
          }
          return 'Your selected query will be updated.'
        }

        if (this.saveState == saveStates.overwriteExisting) {
          if (this.isSomeAdmin && !this.isCurrentUser(this.selectedUser)) {
            return `One of ${username}'s queries (with the same name) will be overwritten.`
          }
          return 'A query with this name already exists and will be overwritten.'
        }

        if (username && username !== 'you') {
          return `A new query will be created for ${username}.`
        }
        return 'A new query will be created for you.'
      },
      submitLabel() {
        if (this.saveState == saveStates.updateExisting) return 'Update existing'
        if (this.saveState == saveStates.overwriteExisting) return 'Overwrite existing'
        return 'Create new'
      },
      submitDisabled() {
        return this.loading.name || this.loading.saving || !this.isValid
      },
      debouncedExists() {
        return debounce(function exec() {
          this.fetchExists()
        }, debounceTimer)
      },
    },
    watch: {
      isSomeAdmin: {
        immediate: true,
        handler(value) {
          if (value) {
            this.fetchUserList()
          }
        },
      },
      'formData.name'() {
        this.$nextTick().then(() => {
          this.feedbackText = ''
          this.nameCheckSequence += 1
          this.loading.name = true
          this.nameExists = false
          this.nameCheckError = ''
          this.debouncedExists()
        })
      },
      selectedUser: {
        deep: true,
        handler() {
          this.$nextTick(() => {
            this.nameCheckSequence += 1
            this.loading.name = true
            this.nameExists = false
            this.nameCheckError = ''
            this.debouncedExists()
          })
        },
      },
    },
    methods: {
      async fetchExists() {
        let service = this.reportGenerator
        const name = this.formData && this.formData.name
        const sequence = ++this.nameCheckSequence

        if (!name) {
          this.nameExists = false
          this.nameCheckError = ''
          this.loading.name = false
          return
        }
        if (!service) {
          this.nameCheckError = 'The saved query service is unavailable. Try again when it is active.'
          this.loading.name = false
          return
        }

        this.loading.name = true

        const userId = (this.selectedUser &&
          (
            this.selectedUser.userId ||
            this.selectedUser.UserId
          )
        )

        let payload = {
          name,
          userId,
        }

        try {
          let exists = await this.$store.dispatch('multistation/checkExisting', {
            service,
            payload,
          })

          if (sequence !== this.nameCheckSequence) return
          this.nameExists = exists
          this.loading.name = false
        } catch (e) {
          console.error(e)
          if (sequence !== this.nameCheckSequence) return
          this.nameCheckError = e && (e.displayMessage || e.reasonPhrase || e.Message || e.message) ||
            'Duplicate names could not be checked right now. You can still save this query.'
          this.loading.name = false
        }
      },
      makePayload() {
        const optionsData = this.optionsData
        let queryParams = JSON.stringify(optionsData)

        return {
          ...this.formData,
          queryParams,
        }
      },
      isCurrentUser(user) {
        if (!user) return true
        let userId = user.userId || user.UserId
        return this.currentUser.userId == userId
      },
      show(queryDetails, queryData, optionsData) {
        this.initial = {
          name: queryDetails.name,
          userId: queryDetails.userId,
        }

        this.queryDetails = queryDetails
        this.queryData = queryData
        this.optionsData = optionsData
        this.feedbackText = ''

        this.dialog = true

        if (!this.isSomeAdmin && queryDetails.userId != this.currentUser.userId) {
          queryDetails.userId = null
          queryDetails.id = null
        }

        this.$nextTick(() => {
          this.$refs.form.setQueryDetails(queryDetails)
        })
      },
      close() {
        this.reset()
        this.dialog = false
      },
      async submit() {
        this.feedbackText = ''
        this.$refs.form.validate()
        await this.$nextTick()
        if (!this.isValid || this.loading.name) {
          if (!this.isValid) this.feedbackText = 'Enter a name to save this query.'
          return
        }

        if (this.saveState.confirmParams) {
          let confirm = await this.$refs.confirmationDialog.confirm({
            ...this.saveState.confirmParams,
          })
          if (!confirm) return
        }

        let action
        if (this.saveState.isUpdate) {
          action = 'multistation/updateQuery'
        } else {
          action = 'multistation/addQuery'
        }

        this.loading.saving = true
        let service = this.reportGenerator
        let query = this.makePayload()

        try {
          const savedQuery = await this.$store.dispatch(action, {
            service,
            query,
          })

          this.loading.saving = false
          this.$store.dispatch('app/setSnackbar', {
            text: 'Query saved successfully',
            color: 'success',
            icon: 'done',
          })

          this.$emit('update:query', savedQuery)
          this.close()

        } catch (e) {
          this.loading.saving = false
          let response = e && e.response

          if (response && response.status < 500) {
            if (response.data.fieldValidations) {
              this.$refs.form.setErrorMessages(response.data.fieldValidations)
            } else {
              this.$store.dispatch('app/setSnackbar', {
                text: `${response.data.displayMessage}. ${response.data.reasonPhrase}`,
                color: 'error',
                icon: 'error',
              })
            }
          } else {
            this.$store.dispatch('app/setSnackbar', {
              text: e && (e.displayMessage || e.reasonPhrase || e.Message || e.message) ||
                'Something unexpected went wrong, please let us know so we can fix it!',
              color: 'error',
              icon: 'error',
            })
          }
        }
      },
      fetchUserList() {
        this.$store.dispatch('admin/getUserList', {
          tenantOnly: true,
        })
      },
      reset() {
        this.queryData = null
        this.nameCheckSequence += 1
        this.nameExists = false
        this.nameCheckError = ''
        this.loading.name = false
        this.feedbackText = ''
      },
    },
  }
</script>

<style scoped>
  .save-dialog {
    display: flex;
    flex-direction: column;
    max-height: min(90vh, 820px);
    overflow: hidden;
    background: #fff;
    color: #333;
  }

  .v-dialog--fullscreen > .save-dialog {
    height: 100%;
    max-height: none;
  }

  .save-dialog-head {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 11px 20px;
    background: #333;
    color: #fff;
  }

  .save-dialog-title {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
  }

  /* Mockup `.save-dialog-x`: transparent white glyph that lightens on hover. */
  .save-dialog-x {
    min-width: 32px;
    margin: 0;
    padding: 4px;
    background: transparent !important;
    box-shadow: none;
  }

  .save-dialog-x .v-icon {
    color: #fff !important;
  }

  .save-dialog-x:hover:not(.v-btn--disabled) {
    background: rgba(255, 255, 255, .1) !important;
  }

  .save-dialog-x.v-btn--disabled .v-icon {
    color: rgba(255, 255, 255, .5) !important;
  }

  .save-dialog-content {
    flex: 1 1 auto;
    overflow: auto;
    padding: 16px 20px;
  }

  /* Mockup `.save-state` + `.warning`. The modifier is named `save-state-warning`
     (not `warning`) because Vuetify's global `.warning` colour utility is
     `!important` and would otherwise win. */
  .save-state {
    margin: 0 0 14px;
    padding: 10px 12px;
    border: 1px solid rgba(82, 153, 67, .35);
    border-radius: 4px;
    background: #eef5eb;
    color: #315e2b;
    font-size: 13px;
  }

  .save-state.save-state-warning {
    border-color: #d9b86f;
    background: #fff7e5;
    color: #654b1c;
  }

  .save-dialog-foot {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid rgba(0, 0, 0, .14);
  }

  .save-dialog-feedback {
    color: #666;
    font-size: 12px;
  }

  .save-dialog-spacer {
    flex: 1;
  }

  /* Mockup `.btn.primary`, with `.warning-button` for the overwrite state. */
  .save-submit {
    height: 40px;
    min-height: 40px;
    margin: 0;
    padding: 0 15px;
    background: #333 !important;
    border-color: #333 !important;
    color: #fff !important;
    font-weight: 500;
    text-transform: none;
    box-shadow: 0 1px 2px rgba(0, 0, 0, .18);
  }

  .save-submit .v-icon {
    color: #fff !important;
  }

  .save-submit:hover:not(.v-btn--disabled) {
    background: #222 !important;
    border-color: #222 !important;
  }

  /* Vuetify's disabled background rule is more specific than a plain
     `.v-btn--disabled` override, so mirror its selector shape to win. */
  .save-submit.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
    background: #ececec !important;
    border-color: #d5d5d5 !important;
    color: #7a7a7a !important;
    opacity: 1;
    box-shadow: none;
  }

  .save-submit.v-btn--disabled .v-icon {
    color: #7a7a7a !important;
  }

  .save-submit.save-submit-warning {
    background: #986500 !important;
    border-color: #986500 !important;
  }

  .save-submit.save-submit-warning:hover:not(.v-btn--disabled) {
    background: #795000 !important;
    border-color: #795000 !important;
  }

  /* Mockup `.btn`: white with a hairline border. */
  .save-cancel {
    height: 40px;
    min-height: 40px;
    margin: 0;
    padding: 0 13px;
    background: #fff !important;
    border: 1px solid rgba(0, 0, 0, .18) !important;
    color: #333 !important;
    font-weight: 500;
    text-transform: none;
  }

  .save-cancel .v-icon {
    color: #333 !important;
  }

  .save-cancel:hover:not(.v-btn--disabled) {
    background: #f7f7f7 !important;
    border-color: #777 !important;
  }
</style>
<style>
  .save-query-dialog {
    display: flex;
    flex-direction: column;
  }
</style>